import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { X, Upload } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MergeTool = ({ currentPdfId, onMergeComplete }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [merging, setMerging] = useState(false);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const newFiles = [];

    try {
      for (const file of files) {
        if (!file.type.includes('pdf')) {
          toast.error(`${file.name} is not a PDF`);
          continue;
        }

        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API}/upload`, formData);
        newFiles.push({
          id: response.data.id,
          name: file.name,
          pages: response.data.num_pages,
        });
      }

      setUploadedFiles([...uploadedFiles, ...newFiles]);
      toast.success(`Uploaded ${newFiles.length} PDF(s)`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload PDFs');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (id) => {
    setUploadedFiles(uploadedFiles.filter(f => f.id !== id));
  };

  const handleMerge = async () => {
    if (uploadedFiles.length === 0) {
      toast.error('Please upload at least one PDF to merge');
      return;
    }

    setMerging(true);
    try {
      const allFileIds = [currentPdfId, ...uploadedFiles.map(f => f.id)].join(',');
      const formData = new FormData();
      formData.append('file_ids', allFileIds);

      const response = await axios.post(`${API}/pdf/merge`, formData);
      const mergedId = response.data.id;
      
      // Load the merged PDF pages to show preview
      const pagesResponse = await axios.get(`${API}/pdf/pages/${mergedId}`);
      
      toast.success(`PDFs merged! Now showing ${pagesResponse.data.pages.length} pages`);
      onMergeComplete(mergedId, pagesResponse.data.pages);
      setUploadedFiles([]);
    } catch (error) {
      console.error('Merge error:', error);
      toast.error('Failed to merge PDFs');
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="merge-tool">
      <div>
        <Label>Upload PDFs to Merge</Label>
        <p className="text-sm text-muted-foreground mt-1.5">
          Upload one or more PDFs to merge with the current document
        </p>
      </div>

      <div>
        <Label htmlFor="merge-upload" className="cursor-pointer">
          <div className="border-2 border-dashed border-border rounded-md p-4 hover:border-primary transition-colors text-center">
            <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {uploading ? 'Uploading...' : 'Click to upload PDFs'}
            </p>
          </div>
        </Label>
        <Input
          id="merge-upload"
          data-testid="merge-upload-input"
          type="file"
          accept=".pdf"
          multiple
          onChange={handleFileUpload}
          className="hidden"
          disabled={uploading}
        />
      </div>

      {uploadedFiles.length > 0 && (
        <div className="space-y-2">
          <Label>Files to Merge ({uploadedFiles.length})</Label>
          <div className="space-y-1">
            {uploadedFiles.map((file) => (
              <div
                key={file.id}
                className="flex items-center justify-between bg-accent/50 p-2 rounded text-xs"
              >
                <div className="flex-1 truncate">
                  <div className="font-medium truncate">{file.name}</div>
                  <div className="text-muted-foreground">{file.pages} pages</div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 ml-2"
                  onClick={() => removeFile(file.id)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Button
        onClick={handleMerge}
        className="w-full"
        disabled={uploadedFiles.length === 0 || merging}
        data-testid="merge-execute-button"
      >
        {merging ? 'Merging...' : `Merge ${uploadedFiles.length + 1} PDFs`}
      </Button>

      <div className="bg-accent/50 p-3 rounded-md">
        <p className="text-xs text-muted-foreground">
          💡 <strong>Tip:</strong> Current PDF will be first, uploaded PDFs will be appended in order.
        </p>
      </div>
    </div>
  );
};

export default MergeTool;